import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Statement,
  StatementJob,
  StripeStatusView,
  TechnicianBalance,
} from "@breeze/shared";
import { TopNav } from "./TopNav.js";
import {
  fetchEarningsTechnicians,
  fetchJobBreakdown,
  fetchPastStatements,
  fetchStatement,
  fetchStripeStatus,
  generateStripeOnboardingLink,
  patchEarningsTechnician,
  recordEarningsPayment,
  refreshStripeStatus,
  setEarningsOpeningBalance,
} from "../api.js";

// ===========================================================================
// Cleaner Earnings dashboard.
//
// Replaces the manual Excel-per-cleaner monthly pay process. Two-pane layout:
//   left rail   — list of active technicians with current balance pill
//   right pane  — selected technician's statement for the chosen period
//
// Money rules (locked with Darren 2026-05-07, see plan + memory):
//   - Card / BACS jobs        → Breeze owes cleaner (TECH_FEE)
//   - Cash jobs               → Cleaner owes Breeze (BREEZE_FEE)
//   - Cheque jobs             → default to Card/BACS bucket; per-tech flag
//                               CHEQUE_TREATED_AS_CASH flips them to Cash
//                               (workaround for the cleaner who takes card
//                               via own machine and records as Cheque)
//   - TOTALPAYMENTMADE < total → flagged on each job (awaitingCustomerPayment).
//                                Job is still bucketed by payment type and STILL
//                                contributes to the cleaner's pay — they're paid
//                                regardless of customer settlement. The flag drives
//                                an on-screen "Awaiting customer payment" visibility
//                                list (print:hidden) so office can chase customers.
//
// Net = openingBalance + cardBacsChequeTotal − cashTotal.
//
// Closing a period writes a frozen snapshot to B_TCLEANER_STATEMENT so
// retroactive B_TVISIT edits don't change history.
// ===========================================================================

type Props = { userName: string };

// ---------------------------------------------------------------------------
// Date helpers — ISO YYYY-MM-DD string in/out.
// ---------------------------------------------------------------------------

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function startOfMonth(year: number, month: number): string {
  return `${year}-${pad(month)}-01`;
}

function endOfMonth(year: number, month: number): string {
  // Day 0 of the next month → last day of the current month.
  const d = new Date(Date.UTC(year, month, 0));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function defaultPeriod(): { year: number; month: number } {
  // Default to the current month. New cleaners' first jobs land in the current
  // month, so opening on the live month avoids the empty-looking board.
  const now = new Date();
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 };
}

const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

function formatMoney(amount: number, symbol: string): string {
  // Match Breeze's display: £1,234.56 with a leading minus. Avoids
  // the "(123.45)" accountancy convention — Darren prefers explicit signs.
  const abs = Math.abs(amount).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `−${symbol}${abs}` : `${symbol}${abs}`;
}

/** Render the percentage share of a fee against the job total, in
 *  brackets after the money value, e.g. "£46.80 (60%)". Returns null
 *  for zero totals so the bracket doesn't show on free / parked jobs.
 *  Used by both the per-row earnings table and the small breakdown
 *  views so the commission split is visible at a glance — added after
 *  Marcus's VID 31287 was paid at 50/50 when his Commission Plan is 60. */
function feePct(fee: number, total: number): JSX.Element | null {
  if (!Number.isFinite(fee) || !Number.isFinite(total) || total <= 0) return null;
  const pct = Math.round((fee / total) * 100);
  if (!Number.isFinite(pct)) return null;
  return (
    <span className="text-[11px] text-ink-300 tabular-nums">({pct}%)</span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

// ---------------------------------------------------------------------------

export function CleanerEarnings({ userName }: Props): JSX.Element {
  const techs = useQuery({
    queryKey: ["earnings", "technicians"],
    queryFn: fetchEarningsTechnicians,
    staleTime: 30_000,
  });

  const techList = useMemo(() => techs.data ?? [], [techs.data]);

  const [selectedTechId, setSelectedTechId] = useState<number | null>(null);

  // On first load — once techs arrive — pick the cleaner with the largest
  // outstanding balance (regardless of sign). That's almost always the one
  // staff want to pay first.
  useEffect(() => {
    if (selectedTechId !== null) return;
    if (techList.length === 0) return;
    const sorted = [...techList].sort(
      (a, b) => Math.abs(b.currentBalance) - Math.abs(a.currentBalance)
    );
    setSelectedTechId(sorted[0]?.technicianId ?? null);
  }, [techList, selectedTechId]);

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav
        userName={userName}
        status={
          techs.isLoading ? <span className="text-[13px]">loading…</span> : null
        }
      />
      <div className="flex flex-1 overflow-hidden">
        <TechnicianRail
          techs={techList}
          loading={techs.isLoading}
          error={techs.error as Error | null}
          selectedId={selectedTechId}
          onSelect={(id) => setSelectedTechId(id)}
        />
        <main className="flex-1 overflow-y-auto p-6 print:p-0">
          {selectedTechId === null ? (
            <EmptyState />
          ) : (
            <StatementView technicianId={selectedTechId} />
          )}
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left rail — technician list with balance pills
// ---------------------------------------------------------------------------

function TechnicianRail({
  techs,
  loading,
  error,
  selectedId,
  onSelect,
}: {
  techs: TechnicianBalance[];
  loading: boolean;
  error: Error | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
}): JSX.Element {
  // Sort: most-owed (in absolute terms) first, settled (zero balance) last.
  const ordered = useMemo(() => {
    return [...techs].sort((a, b) => {
      const aBal = Math.abs(a.currentBalance);
      const bBal = Math.abs(b.currentBalance);
      if (aBal !== bBal) return bBal - aBal;
      return a.nickname.localeCompare(b.nickname);
    });
  }, [techs]);

  return (
    <aside className="flex w-72 flex-col border-r border-ink-700 bg-ink-900 print:hidden">
      <div className="border-b border-ink-700 px-5 py-4">
        <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
          Cleaners
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="px-5 py-4 text-[15px] text-ink-50">Loading…</div>
        )}
        {error && (
          <div className="px-5 py-4 text-[15px] text-slack-pink">
            Couldn't load cleaners: {error.message}
          </div>
        )}
        {!loading && !error && ordered.length === 0 && (
          <div className="px-5 py-4 text-[15px] text-ink-50">
            No active technicians found.
          </div>
        )}
        {ordered.map((t) => (
          <TechRow
            key={t.technicianId}
            tech={t}
            selected={t.technicianId === selectedId}
            onClick={() => onSelect(t.technicianId)}
          />
        ))}
      </div>
    </aside>
  );
}

function TechRow({
  tech,
  selected,
  onClick,
}: {
  tech: TechnicianBalance;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  const balance = tech.currentBalance;
  // Sign convention matches the legacy PDF: positive = Breeze owes cleaner.
  // Pill colour follows comprehension intent (not red=bad/green=good):
  //   - Breeze owes them (positive) → green     (we have an obligation)
  //   - They owe Breeze   (negative) → orange   (cleaner has cash on hand)
  //   - Settled            (zero)    → ink-700  (neutral)
  const tone =
    balance > 0.005
      ? "bg-stage-won/25 text-stage-won"
      : balance < -0.005
      ? "bg-stage-chase/25 text-stage-chase"
      : "bg-ink-700 text-ink-50";
  const balanceText = formatMoney(Math.abs(balance), tech.currencySymbol);
  const balanceLabel =
    balance > 0.005
      ? `Breeze owes ${balanceText}`
      : balance < -0.005
      ? `Owes Breeze ${balanceText}`
      : "Settled";
  // Tooltip explaining each state. "Settled" especially needs help on
  // first use — no closed statements yet means everyone shows £0.00.
  const balanceTooltip =
    balance > 0.005
      ? `Carrying forward ${balanceText}: Breeze still owes ${tech.nickname} this much from the last closed period.`
      : balance < -0.005
      ? `Carrying forward ${balanceText}: ${tech.nickname} still owes Breeze this much from the last closed period.`
      : "Balance is £0.00 — either no closed statements yet or fully settled at the last payment.";
  return (
    <button
      onClick={onClick}
      className={`block w-full border-b border-ink-700 px-5 py-3 text-left transition-colors ${
        selected ? "bg-ink-700" : "hover:bg-ink-800"
      }`}
    >
      <div className="text-[15px] font-semibold text-ink-50">{tech.nickname}</div>
      <div className="mt-2 flex items-center gap-2">
        <span
          className={`rounded-md px-2 py-0.5 text-[13px] font-semibold ${tone}`}
          title={balanceTooltip}
        >
          {balanceLabel}
        </span>
      </div>
      {tech.lastPaymentSentDate && (
        <div className="mt-1 text-[13px] text-ink-100">
          Last paid {formatDate(tech.lastPaymentSentDate)}
        </div>
      )}
    </button>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-[15px] text-ink-50">Select a cleaner to see their statement.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right pane — statement view
// ---------------------------------------------------------------------------

function StatementView({ technicianId }: { technicianId: number }): JSX.Element {
  const initial = defaultPeriod();
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);

  const periodStart = startOfMonth(year, month);
  const periodEnd = endOfMonth(year, month);

  const statement = useQuery({
    queryKey: ["earnings", "statement", technicianId, periodStart, periodEnd],
    queryFn: () => fetchStatement(technicianId, periodStart, periodEnd),
  });

  const past = useQuery({
    queryKey: ["earnings", "statements", technicianId],
    queryFn: () => fetchPastStatements(technicianId),
  });

  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);

  const data = statement.data;

  return (
    <div className="print-statement max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div>
          <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
            Statement
          </div>
          <h1 className="mt-1 text-2xl font-semibold text-ink-50">
            {data?.technicianName ?? "Loading…"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <PeriodPicker
            year={year}
            month={month}
            onYear={(y) => setYear(y)}
            onMonth={(m) => setMonth(m)}
          />
        </div>
      </div>

      {statement.isLoading && (
        <div className="mt-6 text-[15px] text-ink-50">Loading statement…</div>
      )}
      {statement.isError && (
        <div className="mt-6 text-[15px] text-slack-pink">
          {(statement.error as Error).message}
        </div>
      )}
      {data && (
        <>
          <PrintHeader statement={data} />
          <ChequeFlagToggle technicianId={technicianId} statement={data} />
          <SummaryCard statement={data} />

          <StripeConnectBar
            technicianId={technicianId}
            technicianName={data.technicianName}
            samedayPaymentEnabled={data.samedayPaymentEnabled}
          />

          <div className="mt-4 flex flex-wrap gap-2 print:hidden">
            {data.status === "draft" && (
              <button
                onClick={() => setRecordPaymentOpen(true)}
                className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight"
              >
                Record payment
              </button>
            )}
            <button
              onClick={() => window.print()}
              className="rounded-md border border-ink-500 px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-ink-700"
            >
              Print / Save as PDF
            </button>
            <button
              onClick={() => setOpeningOpen(true)}
              className="rounded-md border border-ink-500 px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-ink-700"
            >
              Set opening balance
            </button>
          </div>

          {data.status === "closed" && (
            <div className="mt-4 rounded-md bg-stage-won/15 px-4 py-3 text-[15px] text-stage-won print:bg-transparent print:px-0 print:py-1 print:text-black">
              Period closed. Payment of{" "}
              {formatMoney(data.paymentAmount ?? 0, data.currencySymbol)} sent on{" "}
              {formatDate(data.paymentSentDate)}. Balance carried forward:{" "}
              {formatMoney(data.balanceCarriedForward ?? 0, data.currencySymbol)}.
            </div>
          )}

          <JobsTable
            heading="Card, BACS, Cheque"
            subheading="Breeze received the customer's money — owes the cleaner the Tech Fee on each row."
            jobs={data.cardBacsChequeJobs}
            currency={data.currencySymbol}
            valueColumn="techFee"
            totalLabel="Card, Cheque, BACS"
            totalAmount={data.cardBacsChequeTotal}
            samedayPaidTotal={data.samedayPaidTotal}
            samedayPaidJobCount={data.samedayPaidJobCount}
          />
          <JobsTable
            heading="Cash"
            subheading="Cleaner kept the customer's cash — owes Breeze the Breeze Fee on each row."
            jobs={data.cashJobs}
            currency={data.currencySymbol}
            valueColumn="breezeFee"
            totalLabel="Cash"
            totalAmount={data.cashTotal}
            negate
          />
          {data.awaitingCustomerPaymentJobs.length > 0 && (
            <section className="print-section mt-6 rounded-xl border border-ink-700 bg-ink-700/40 p-5 shadow-card print:hidden">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-xl font-semibold text-ink-50">
                  Awaiting customer payment
                </h2>
                <div className="text-xl font-semibold tabular-nums text-ink-100">
                  Outstanding{" "}
                  {formatMoney(
                    data.awaitingCustomerPaymentJobs.reduce(
                      (s, j) => s + (j.totalCost - j.totalPaid),
                      0
                    ),
                    data.currencySymbol
                  )}
                </div>
              </div>
              <p className="mt-1 text-[13px] text-ink-100">
                These jobs are already counted in the cleaner totals above — the
                cleaner is paid regardless. Listed here so the office can chase
                the customer for the outstanding balance.
              </p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-[15px] text-ink-50">
                  <thead>
                    <tr className="border-b border-ink-500 text-left text-[13px] uppercase tracking-wider text-ink-100">
                      <th className="py-2 pr-3 font-semibold">Order date</th>
                      <th className="py-2 pr-3 font-semibold">Job no</th>
                      <th className="py-2 pr-3 font-semibold">Account</th>
                      <th className="py-2 pr-3 font-semibold">Postcode</th>
                      <th className="py-2 pr-3 text-right font-semibold">Total</th>
                      <th className="py-2 pr-3 text-right font-semibold">Paid</th>
                      <th className="py-2 pr-3 text-right font-semibold">Outstanding</th>
                      <th className="py-2 pr-3 font-semibold">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.awaitingCustomerPaymentJobs.map((j) => (
                      <tr key={j.vid} className="border-b border-ink-700/60 last:border-b-0">
                        <td className="py-2 pr-3 tabular-nums">{formatDate(j.scheduleDate)}</td>
                        <td className="py-2 pr-3 tabular-nums">{j.visitNumber}</td>
                        <td className="py-2 pr-3">{j.accountName ?? "—"}</td>
                        <td className="py-2 pr-3">{j.postcode ?? "—"}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {formatMoney(j.totalCost, data.currencySymbol)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {formatMoney(j.totalPaid, data.currencySymbol)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums font-semibold text-stage-chase">
                          {formatMoney(j.totalCost - j.totalPaid, data.currencySymbol)}
                        </td>
                        <td className="py-2 pr-3">{j.paymentType ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      <PastStatements
        statements={past.data ?? []}
        loading={past.isLoading}
        currentPeriodStart={periodStart}
        currentPeriodEnd={periodEnd}
        onPick={(s) => {
          const [y, m] = s.periodStart.split("-").map(Number);
          if (Number.isFinite(y) && Number.isFinite(m)) {
            setYear(y!);
            setMonth(m!);
          }
        }}
      />

      {recordPaymentOpen && data && (
        <RecordPaymentModal
          statement={data}
          onClose={() => setRecordPaymentOpen(false)}
        />
      )}
      {openingOpen && data && (
        <SetOpeningBalanceModal
          technicianId={technicianId}
          currencySymbol={data.currencySymbol}
          onClose={() => setOpeningOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Period picker — month + year dropdowns. Years range from 2024 to current.
// ---------------------------------------------------------------------------

function PeriodPicker({
  year,
  month,
  onYear,
  onMonth,
}: {
  year: number;
  month: number;
  onYear: (y: number) => void;
  onMonth: (m: number) => void;
}): JSX.Element {
  const years = useMemo(() => {
    const now = new Date().getUTCFullYear();
    const out: number[] = [];
    for (let y = now; y >= 2024; y--) out.push(y);
    return out;
  }, []);
  return (
    <div className="flex items-center gap-2">
      <select
        value={month}
        onChange={(e) => onMonth(Number(e.target.value))}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
      >
        {MONTH_LABELS.map((label, i) => (
          <option key={i} value={i + 1}>
            {label}
          </option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => onYear(Number(e.target.value))}
        className="rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Print header — only visible when printing. Mirrors the legacy PDF first
// page so a printed sheet looks like what staff are used to filing.
// ---------------------------------------------------------------------------

function PrintHeader({ statement }: { statement: Statement }): JSX.Element {
  return (
    <div className="hidden print:block">
      <h1 className="text-2xl font-semibold text-black">
        {statement.technicianName} — Statement
      </h1>
      <div className="text-base text-black">
        Period: {formatDate(statement.periodStart)} → {formatDate(statement.periodEnd)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cheque-as-cash toggle — surfaces the per-tech CHEQUE_TREATED_AS_CASH flag.
// ---------------------------------------------------------------------------

function ChequeFlagToggle({
  technicianId,
  statement,
}: {
  technicianId: number;
  statement: Statement;
}): JSX.Element {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (next: boolean) =>
      patchEarningsTechnician(technicianId, { chequeTreatedAsCash: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["earnings", "technicians"] });
      void qc.invalidateQueries({ queryKey: ["earnings", "statement", technicianId] });
    },
  });
  return (
    <div className="mt-4 rounded-md border border-ink-700 bg-ink-700/40 px-4 py-3 print:hidden">
      <label className="flex cursor-pointer items-center gap-3 text-[15px] text-ink-50">
        <input
          type="checkbox"
          checked={statement.chequeTreatedAsCash}
          onChange={(e) => m.mutate(e.target.checked)}
          disabled={m.isPending || statement.status === "closed"}
          className="h-5 w-5 cursor-pointer accent-slack-aubergine"
        />
        <span>
          Treat <strong>Cheque</strong> payments as <strong>Cash</strong> for this cleaner.
        </span>
      </label>
      <div className="mt-1 pl-8 text-[13px] text-ink-100">
        Use this for cleaners who take card on their own machine and record the visit as
        Cheque (real cheques are rare). When on, Cheque jobs bucket into the Cash side.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stripe Connect bar — status pill + onboarding-link generator.
//
// Compact horizontal bar showing the technician's Stripe Connect status.
// Lets admin generate a fresh Stripe-hosted onboarding link (expires in
// ~5 minutes) to send via WhatsApp. Hidden when printing.
// ---------------------------------------------------------------------------

function StripeConnectBar({
  technicianId,
  technicianName,
  samedayPaymentEnabled,
}: {
  technicianId: number;
  technicianName: string;
  samedayPaymentEnabled: boolean;
}): JSX.Element | null {
  const qc = useQueryClient();
  const status = useQuery<StripeStatusView>({
    queryKey: ["stripe-connect", "status", technicianId],
    queryFn: () => fetchStripeStatus(technicianId),
    // Don't blow up the page if Stripe isn't configured on the server.
    retry: false,
  });
  const [showLink, setShowLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onboardMutation = useMutation({
    mutationFn: () => generateStripeOnboardingLink(technicianId),
    onSuccess: (link) => {
      setShowLink({ url: link.url, expiresAt: link.expiresAt });
      setError(null);
      void qc.invalidateQueries({ queryKey: ["stripe-connect", "status", technicianId] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to generate link");
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => refreshStripeStatus(technicianId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["stripe-connect", "status", technicianId] });
    },
  });

  // Same-day toggle — only enableable once the tech has payouts_enabled
  // from Stripe (i.e. KYC complete). Calls the same PATCH endpoint as the
  // cheque-as-cash flag.
  const samedayMutation = useMutation({
    mutationFn: (next: boolean) =>
      patchEarningsTechnician(technicianId, { samedayPaymentEnabled: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["earnings"] });
    },
  });

  // Hide the bar entirely if Stripe isn't configured — keeps the dashboard
  // usable in dev environments without leaking 503s into the UI.
  if (status.isError && status.error instanceof Error && status.error.message.includes("Stripe Connect is not configured")) {
    return null;
  }

  const view = status.data;
  const pillTone = (() => {
    if (!view) return "bg-ink-700 text-ink-100";
    switch (view.status) {
      case "active":
        return view.payoutsEnabled
          ? "bg-stage-won/25 text-stage-won"
          : "bg-stage-opportunity/25 text-stage-opportunity";
      case "onboarding":
        return "bg-stage-lead/25 text-stage-lead";
      case "restricted":
        return "bg-stage-lost/25 text-stage-lost";
      case "not_started":
      default:
        return "bg-ink-700 text-ink-50";
    }
  })();

  const buttonLabel = view?.accountId ? "New onboarding link" : "Onboard to Stripe";

  return (
    <div className="mt-4 rounded-md border border-ink-700 bg-ink-700/40 px-4 py-3 print:hidden">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-100">
          Stripe Connect
        </div>
        <span className={`rounded-md px-2 py-0.5 text-[13px] font-semibold ${pillTone}`}>
          {status.isLoading ? "Loading…" : view?.displayLabel ?? "Unknown"}
        </span>
        {view?.accountId && (
          <span className="font-mono text-[11px] text-ink-100" title={view.accountId}>
            {view.accountId.slice(0, 12)}…
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => onboardMutation.mutate()}
            disabled={onboardMutation.isPending}
            className="rounded-md bg-slack-aubergine px-3 py-1.5 text-[13px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
          >
            {onboardMutation.isPending ? "Generating…" : buttonLabel}
          </button>
          {view?.accountId && (
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] font-semibold text-ink-50 hover:bg-ink-700 disabled:opacity-50"
              title="Pull latest status from Stripe (use if webhook lagged)"
            >
              {refreshMutation.isPending ? "Refreshing…" : "Refresh"}
            </button>
          )}
        </div>
      </div>
      {/* Same-day pay toggle. Only meaningful once payouts_enabled, but we
          render it always so the admin sees it exists and can read the
          tooltip explaining what they need to do first. */}
      <div className="mt-3 border-t border-ink-700 pt-3">
        <label
          className={`flex items-start gap-3 text-[14px] ${
            view?.payoutsEnabled ? "cursor-pointer text-ink-50" : "cursor-not-allowed text-ink-100"
          }`}
          title={
            view?.payoutsEnabled
              ? "When on, paylinks for this tech's jobs split funds at customer-payment time via Stripe Connect (tech gets TECH_FEE in their bank, Breeze keeps BREEZE_FEE minus the Stripe processing fee)."
              : "Disabled until Stripe Connect onboarding is fully complete (payouts_enabled = true)."
          }
        >
          <input
            type="checkbox"
            checked={samedayPaymentEnabled}
            onChange={(e) => samedayMutation.mutate(e.target.checked)}
            disabled={!view?.payoutsEnabled || samedayMutation.isPending}
            className="mt-1 h-4 w-4 cursor-pointer accent-slack-aubergine disabled:cursor-not-allowed"
          />
          <span>
            <strong>Same-day pay</strong> — split each card payment via Stripe Connect (tech is
            paid the moment the customer pays). Cash, BACS and cheque jobs still settle on the
            monthly statement.
            {!view?.payoutsEnabled && (
              <span className="ml-2 text-[12px] text-stage-opportunity">
                (complete Stripe onboarding to enable)
              </span>
            )}
          </span>
        </label>
      </div>
      {error && (
        <div className="mt-2 text-[13px] text-stage-lost">{error}</div>
      )}
      {showLink && (
        <OnboardingLinkPanel
          url={showLink.url}
          expiresAt={showLink.expiresAt}
          technicianName={technicianName}
          onClose={() => setShowLink(null)}
        />
      )}
    </div>
  );
}

function OnboardingLinkPanel({
  url,
  expiresAt,
  technicianName,
  onClose,
}: {
  url: string;
  expiresAt: string;
  technicianName: string;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const expiresIn = useMemo(() => {
    const ms = new Date(expiresAt).getTime() - Date.now();
    const minutes = Math.max(0, Math.round(ms / 60000));
    return minutes <= 1 ? "<1 minute" : `${minutes} minutes`;
  }, [expiresAt]);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers — fallback: select the text input.
      const el = document.getElementById("stripe-onboard-url") as HTMLInputElement | null;
      el?.select();
    }
  }

  return (
    <div className="mt-3 rounded-md border border-ink-500 bg-ink-800 p-3">
      <div className="text-[13px] text-ink-50">
        <strong>Send this link to {technicianName} via WhatsApp.</strong> Link expires in{" "}
        <span className="text-stage-opportunity">{expiresIn}</span>. If they don't click in
        time, regenerate.
      </div>
      <div className="mt-2 flex gap-2">
        <input
          id="stripe-onboard-url"
          type="text"
          value={url}
          readOnly
          onClick={(e) => e.currentTarget.select()}
          className="flex-1 rounded-md border border-ink-500 bg-ink-700 px-2 py-1 font-mono text-[12px] text-ink-50"
        />
        <button
          onClick={() => void copy()}
          className="rounded-md bg-slack-aubergine px-3 py-1 text-[13px] font-semibold text-ink-50 hover:bg-slack-aubergineLight"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
        <button
          onClick={onClose}
          className="rounded-md border border-ink-500 px-3 py-1 text-[13px] font-semibold text-ink-50 hover:bg-ink-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary card — opening balance + bucket totals + net.
// ---------------------------------------------------------------------------

function SummaryCard({ statement }: { statement: Statement }): JSX.Element {
  const c = statement.currencySymbol;
  const lines: Array<{ label: string; amount: number; subtle?: boolean; emphasis?: boolean }> = [
    {
      label: "Opening balance (last carry forward)",
      amount: statement.openingBalance,
      subtle: true,
    },
    {
      label: "Card, Cheque, BACS",
      amount: statement.cardBacsChequeTotal,
    },
    {
      label: "Cash",
      // Cash flips sign in the equation — display as a negative so the
      // arithmetic is obvious to staff.
      amount: -statement.cashTotal,
    },
  ];
  return (
    <section className="print-summary mt-6 rounded-xl border border-ink-700 bg-ink-700 p-6 shadow-card print:border print:border-black print:bg-white print:text-black print:shadow-none">
      <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100 print:text-black">
        {statement.technicianName} —{" "}
        {formatDate(statement.periodStart)} to {formatDate(statement.periodEnd)}
      </div>
      <div className="mt-4 space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="flex items-baseline justify-between text-[15px] text-ink-50 print:text-black">
            <span>{l.label}</span>
            <span className="font-semibold tabular-nums">
              {formatMoney(l.amount, c)}
            </span>
          </div>
        ))}
        <div className="my-3 border-t border-ink-500 print:border-black" />
        <div className="flex items-baseline justify-between text-xl font-semibold text-ink-50 print:text-black">
          <span>Net due to cleaner</span>
          <span
            className={`tabular-nums ${
              statement.netDueToCleaner < 0 ? "text-stage-chase" : "text-stage-won"
            } print:text-black`}
          >
            {formatMoney(statement.netDueToCleaner, c)}
          </span>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Jobs table — three of these (cardBacsCheque, cash, awaiting).
// ---------------------------------------------------------------------------

function JobsTable({
  heading,
  subheading,
  jobs,
  currency,
  valueColumn,
  totalLabel: _totalLabel,
  totalAmount,
  negate = false,
  greyTotal = false,
  samedayPaidTotal = 0,
  samedayPaidJobCount = 0,
}: {
  heading: string;
  subheading: string;
  jobs: StatementJob[];
  currency: string;
  valueColumn: "techFee" | "breezeFee" | "totalCost";
  totalLabel: string;
  totalAmount: number;
  negate?: boolean;
  greyTotal?: boolean;
  samedayPaidTotal?: number;
  samedayPaidJobCount?: number;
}): JSX.Element {
  const headers = ["", "Order Date", "Job No", "Address", "Postcode", "Account", "Tech Fee", "Breeze Fee", "Payment", "Total"];
  // When Flow A has paid any of these jobs already, surface a deduction line
  // so the admin sees the net Breeze owes — never just the gross. Hidden in
  // print since the cleaner already saw the same-day amounts hit their bank.
  const showSamedayDeduction = samedayPaidTotal > 0;
  const netOwed = Math.max(0, totalAmount - samedayPaidTotal);
  return (
    <section className="print-section mt-6 rounded-xl border border-ink-700 bg-ink-700/60 p-5 shadow-card print:border print:border-black print:bg-white print:text-black print:shadow-none">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-semibold text-ink-50 print:text-black">{heading}</h2>
        <div className={`text-xl font-semibold tabular-nums ${greyTotal ? "text-ink-100" : "text-ink-50"} print:text-black`}>
          {formatMoney(negate ? -totalAmount : totalAmount, currency)}
        </div>
      </div>
      {showSamedayDeduction && (
        <div
          className="mt-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-2 rounded-lg border border-purple-500/30 bg-purple-500/10 px-4 py-2.5 text-[13px] print:hidden"
        >
          <span className="flex items-center gap-2 whitespace-nowrap text-ink-50">
            <span className="text-[15px] text-purple-300" aria-hidden="true">⚡</span>
            <span>
              <span className="font-semibold text-purple-200">{samedayPaidJobCount}</span>{" "}
              {samedayPaidJobCount === 1 ? "job" : "jobs"} paid same-day via Stripe Connect
            </span>
          </span>
          <span className="flex flex-wrap items-baseline gap-x-4 gap-y-1 tabular-nums">
            <span className="whitespace-nowrap text-ink-100">
              Tech received{" "}
              <span className="font-semibold text-purple-200">
                {formatMoney(samedayPaidTotal, currency)}
              </span>
            </span>
            <span className="whitespace-nowrap text-ink-100">
              Breeze owes{" "}
              <span className="font-semibold text-stage-won">
                {formatMoney(netOwed, currency)}
              </span>{" "}
              this period
            </span>
          </span>
        </div>
      )}
      <p className="mt-1 text-[13px] text-ink-100 print:hidden">{subheading}</p>
      {jobs.length === 0 ? (
        <div className="mt-3 text-[15px] text-ink-100 print:text-black">No jobs in this bucket.</div>
      ) : (
        <div className="mt-3 overflow-x-auto print:overflow-visible">
          <table className="w-full text-[15px] text-ink-50 print:text-black">
            <thead>
              <tr className="border-b border-ink-500 text-left text-[13px] uppercase tracking-wider text-ink-100 print:text-black">
                {headers.map((h) => {
                  const isMoney = h === "Tech Fee" || h === "Breeze Fee" || h === "Total";
                  const isWrap = h === "Address" || h === "Account";
                  return (
                    <th
                      key={h || "expand"}
                      className={`py-2 pr-3 font-semibold ${isMoney ? "text-right" : ""} ${isWrap ? "print-wrap" : ""}`}
                    >
                      {h}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <JobRow key={j.vid} j={j} currency={currency} valueColumn={valueColumn} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// One row in JobsTable. Click the chevron to expand the line-item + Stripe
// Connect breakdown inline. Hidden in print so the printed statement stays
// the same single-line-per-job format staff are used to filing.
function JobRow({
  j,
  currency,
  valueColumn,
}: {
  j: StatementJob;
  currency: string;
  valueColumn: "techFee" | "breezeFee" | "totalCost";
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr className="border-b border-ink-700/60 last:border-b-0 print:border-black/20">
        <td className="py-2 pr-1 print:hidden">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="rounded-md px-1.5 py-0.5 text-[12px] text-ink-100 hover:bg-ink-700 hover:text-ink-50"
            title={expanded ? "Hide breakdown" : "Show breakdown"}
            aria-label={expanded ? "Hide breakdown" : "Show breakdown"}
          >
            {expanded ? "▾" : "▸"}
          </button>
        </td>
        <td className="py-2 pr-3 tabular-nums">{formatDate(j.scheduleDate)}</td>
        <td className="py-2 pr-3 tabular-nums">{j.visitNumber}</td>
        <td className="print-wrap py-2 pr-3">{j.serviceAddressLine1 ?? "—"}</td>
        <td className="py-2 pr-3">{j.postcode ?? "—"}</td>
        <td className="print-wrap py-2 pr-3">{j.accountName ?? "—"}</td>
        <td className={`whitespace-nowrap py-2 pr-3 text-right tabular-nums ${valueColumn === "techFee" ? "font-semibold" : ""}`}>
          <span className="inline-flex items-center justify-end gap-1.5">
            {j.stripeAlreadyPaid > 0 && (
              <span
                className="text-[13px] leading-none text-purple-300 print:hidden"
                title={`Same-day pay: ${formatMoney(j.stripeAlreadyPaid, currency)} already routed to cleaner via Stripe Connect`}
                aria-label="Same-day pay routed via Stripe Connect"
              >
                ⚡
              </span>
            )}
            <span>{formatMoney(j.techFee, currency)}</span>
            {feePct(j.techFee, j.totalCost)}
          </span>
        </td>
        <td className={`py-2 pr-3 text-right tabular-nums ${valueColumn === "breezeFee" ? "font-semibold" : ""}`}>
          <span className="inline-flex items-center justify-end gap-1.5">
            <span>{formatMoney(j.breezeFee, currency)}</span>
            {feePct(j.breezeFee, j.totalCost)}
          </span>
        </td>
        <td className="py-2 pr-3">{j.paymentType ?? "—"}</td>
        <td className="py-2 pr-3 text-right tabular-nums">
          {formatMoney(j.totalCost, currency)}
        </td>
      </tr>
      {expanded && (
        <tr className="print:hidden">
          <td></td>
          <td colSpan={9} className="bg-ink-800/40 py-3 pr-3">
            <JobBreakdownView vid={j.vid} currency={currency} />
          </td>
        </tr>
      )}
    </>
  );
}

function JobBreakdownView({ vid, currency }: { vid: number; currency: string }): JSX.Element {
  const q = useQuery({
    queryKey: ["earnings", "job-breakdown", vid],
    queryFn: () => fetchJobBreakdown(vid),
    staleTime: 60_000,
  });
  if (q.isLoading) {
    return <div className="text-[13px] text-ink-100">Loading breakdown…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="text-[13px] text-stage-lost">
        {q.error instanceof Error ? q.error.message : "Couldn't load breakdown"}
      </div>
    );
  }
  const b = q.data;
  return (
    <div className="space-y-3 text-[13px] text-ink-50">
      {/* Line items */}
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-[0.14em] text-ink-100">Line items</div>
        {b.lineItems.length === 0 ? (
          <div className="text-ink-100">No line items recorded.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-ink-700 text-left text-[11px] uppercase tracking-wider text-ink-100">
                <th className="py-1 pr-3 font-semibold">Qty</th>
                <th className="py-1 pr-3 font-semibold">Description</th>
                <th className="py-1 pr-3 text-right font-semibold">Unit</th>
                <th className="py-1 pr-3 text-right font-semibold">Discount</th>
                <th className="py-1 pr-3 text-right font-semibold">Disc %</th>
                <th className="py-1 pr-3 font-semibold">Absorber</th>
                <th className="py-1 pr-3 text-right font-semibold">Subtotal</th>
                <th className="py-1 pr-3 text-right font-semibold">Tech earn</th>
                <th className="py-1 text-right font-semibold">Breeze earn</th>
              </tr>
            </thead>
            <tbody>
              {b.lineItems.map((li) => {
                // gross = post-discount subtotal + the £ discount that
                // was taken off. Discount % is expressed against gross
                // so "£5.25 off £35" shows 15%, matching the form.
                const gross = li.subtotal + li.discountAmount;
                const discountPct =
                  li.discountAmount > 0 && gross > 0
                    ? (li.discountAmount / gross) * 100
                    : 0;
                const absorberLabel = li.discountAbsorber === "split" ? "Split" : "Breeze";
                return (
                  <tr key={li.chargeId} className="border-b border-ink-700/40 last:border-b-0">
                    <td className="py-1 pr-3 tabular-nums">{li.quantity}</td>
                    <td className="py-1 pr-3">{li.description || "—"}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">
                      {formatMoney(li.unitSellPrice, currency)}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums text-ink-100">
                      {li.discountAmount > 0 ? formatMoney(li.discountAmount, currency) : "—"}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums text-ink-100">
                      {discountPct > 0 ? `${discountPct.toFixed(1)}%` : "—"}
                    </td>
                    <td className="py-1 pr-3 text-ink-100">
                      {li.discountAmount > 0 ? absorberLabel : "—"}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums">{formatMoney(li.subtotal, currency)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{formatMoney(li.techEarn, currency)}</td>
                    <td className="py-1 text-right tabular-nums">{formatMoney(li.breezeEarn, currency)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-ink-500 font-semibold">
                <td colSpan={6} className="py-1.5 pr-3 text-right text-ink-100">Visit totals</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{formatMoney(b.totalCost, currency)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  <span className="inline-flex items-center justify-end gap-1.5">
                    <span>{formatMoney(b.techFee, currency)}</span>
                    {feePct(b.techFee, (b.techFee ?? 0) + (b.breezeFee ?? 0))}
                  </span>
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  <span className="inline-flex items-center justify-end gap-1.5">
                    <span>{formatMoney(b.breezeFee, currency)}</span>
                    {feePct(b.breezeFee, (b.techFee ?? 0) + (b.breezeFee ?? 0))}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Visit-level rollup line: subtotal / discount / tax / total */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12px] sm:grid-cols-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-100">Subtotal</div>
          <div className="tabular-nums">{formatMoney(b.subtotal, currency)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-100">Discount</div>
          <div className="tabular-nums">
            {b.discountTotal > 0 ? `−${formatMoney(b.discountTotal, currency)}` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-100">Tax</div>
          <div className="tabular-nums">{formatMoney(b.taxTotal, currency)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-100">Customer paid</div>
          <div className="tabular-nums">{formatMoney(b.totalPaid, currency)}</div>
        </div>
      </div>

      {/* Stripe Connect transactions linked to this job */}
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-[0.14em] text-ink-100">
          Stripe Connect payments
        </div>
        {b.stripePayments.length === 0 ? (
          <div className="text-ink-100">
            No Stripe Connect payment on this job (paid via cash / BACS / cheque, OR same-day pay
            not enabled for this tech, OR customer hasn't paid yet).
          </div>
        ) : (
          <div className="space-y-2">
            {b.stripePayments.map((p) => (
              <div
                key={p.id}
                className="rounded-md border border-ink-700 bg-ink-800/60 p-2 text-[12px]"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span
                    className={`rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                      p.status === "payout_paid"
                        ? "bg-stage-won/25 text-stage-won"
                        : p.status === "payout_failed"
                          ? "bg-stage-lost/25 text-stage-lost"
                          : p.status === "paid" || p.status === "payout_pending"
                            ? "bg-stage-lead/25 text-stage-lead"
                            : "bg-ink-700 text-ink-100"
                    }`}
                  >
                    {p.status.replace("_", " ")}
                  </span>
                  <span className="tabular-nums">
                    Customer paid {formatMoney(p.amountTotal, currency)}
                  </span>
                  <span className="text-ink-100">·</span>
                  <span className="tabular-nums text-stage-won">
                    Tech got {formatMoney(p.techAmount, currency)}
                  </span>
                  <span className="text-ink-100">·</span>
                  <span className="tabular-nums">
                    Breeze {formatMoney(p.applicationFeeAmount, currency)}
                    {p.stripeFeeAmount !== null && (
                      <span className="text-ink-100">
                        {" "}
                        (net {formatMoney(p.applicationFeeAmount - p.stripeFeeAmount, currency)}{" "}
                        after Stripe {formatMoney(p.stripeFeeAmount, currency)})
                      </span>
                    )}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono text-ink-100">
                  {p.paymentIntentId && (
                    <a
                      href={`https://dashboard.stripe.com/payments/${p.paymentIntentId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-ink-50 hover:underline"
                    >
                      {p.paymentIntentId}
                    </a>
                  )}
                  {p.transferId && (
                    <span title="Transfer to connected account">{p.transferId}</span>
                  )}
                  {p.payoutId && <span title="Instant payout to tech bank">{p.payoutId}</span>}
                </div>
                {p.failureMessage && (
                  <div className="mt-1 text-[12px] text-stage-lost">{p.failureMessage}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Past statements (closed periods) — clickable list to jump back to history.
// ---------------------------------------------------------------------------

function PastStatements({
  statements,
  loading,
  onPick,
  currentPeriodStart,
  currentPeriodEnd,
}: {
  statements: Statement[];
  loading: boolean;
  onPick: (s: Statement) => void;
  currentPeriodStart: string;
  currentPeriodEnd: string;
}): JSX.Element {
  const closed = statements.filter((s) => s.status === "closed");
  if (loading) return <></>;
  if (closed.length === 0) return <></>;
  return (
    <section className="mt-8 rounded-xl border border-ink-700 bg-ink-700/40 p-5 print:hidden">
      <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
        Past statements
      </div>
      <ul className="mt-3 divide-y divide-ink-700">
        {closed.map((s) => {
          const isCurrent =
            s.periodStart === currentPeriodStart && s.periodEnd === currentPeriodEnd;
          return (
            <li key={`${s.periodStart}-${s.periodEnd}`}>
              <button
                onClick={() => onPick(s)}
                className={`flex w-full items-baseline justify-between gap-4 rounded-md px-3 py-3 text-left text-ink-50 transition-colors ${
                  isCurrent ? "bg-ink-700" : "hover:bg-ink-700/60"
                }`}
              >
                <div>
                  <div className="text-[15px] font-semibold">
                    {formatDate(s.periodStart)} → {formatDate(s.periodEnd)}
                  </div>
                  <div className="text-[13px] text-ink-100">
                    Paid {formatMoney(s.paymentAmount ?? 0, s.currencySymbol)} on{" "}
                    {formatDate(s.paymentSentDate)} · carried{" "}
                    {formatMoney(s.balanceCarriedForward ?? 0, s.currencySymbol)}
                  </div>
                </div>
                <div className="text-[15px] font-semibold tabular-nums">
                  {formatMoney(s.netDueToCleaner, s.currencySymbol)}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

function RecordPaymentModal({
  statement,
  onClose,
}: {
  statement: Statement;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<string>(statement.netDueToCleaner.toFixed(2));
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"manual" | "stripe">("manual");
  // When user picks Stripe, show a confirm step before firing the actual
  // transfer — Stripe payouts are irreversible from our side, so a quick
  // "yes, send £X via Stripe Instant Payout" gate is worth the friction.
  const [stripeConfirming, setStripeConfirming] = useState(false);

  // Load Stripe status for this tech to know if the Stripe option should
  // even be selectable. Quiet failure (no Stripe configured at all) just
  // hides the option.
  const stripeStatus = useQuery<StripeStatusView>({
    queryKey: ["stripe-connect", "status", statement.technicianId],
    queryFn: () => fetchStripeStatus(statement.technicianId),
    retry: false,
  });
  const stripeAvailable =
    !!stripeStatus.data?.payoutsEnabled && !!stripeStatus.data?.accountId;

  const m = useMutation({
    mutationFn: () =>
      recordEarningsPayment(statement.technicianId, {
        periodStart: statement.periodStart,
        periodEnd: statement.periodEnd,
        paymentAmount: Number(amount),
        paymentSentDate: date,
        notes: notes.trim() || null,
        paymentMethod,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["earnings"] });
      onClose();
    },
  });

  const valid = Number.isFinite(Number(amount)) && /^\d{4}-\d{2}-\d{2}$/.test(date);
  const amountNum = Number(amount);
  // 1.5% Instant Payout fee — Breeze absorbs. Shown so the admin sees
  // the cost-per-payment when picking Stripe.
  const stripeFee = Number.isFinite(amountNum) ? Math.round(amountNum * 0.015 * 100) / 100 : 0;

  function handleSubmit(): void {
    if (paymentMethod === "stripe" && !stripeConfirming) {
      setStripeConfirming(true);
      return;
    }
    m.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-4">
      <div className="w-full max-w-md rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card">
        <h2 className="text-xl font-semibold text-ink-50">Record payment</h2>
        <p className="mt-1 text-[15px] text-ink-50">
          Closing {formatDate(statement.periodStart)} → {formatDate(statement.periodEnd)} for{" "}
          {statement.technicianName}.
        </p>
        <div className="mt-4 space-y-3 text-[15px]">
          <div>
            <label className="block text-[13px] uppercase tracking-wider text-ink-100">
              Amount sent ({statement.currencySymbol})
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
            <p className="mt-1 text-[13px] text-ink-100">
              Net due:{" "}
              {formatMoney(statement.netDueToCleaner, statement.currencySymbol)}. Pay
              less to roll a balance into next month.
            </p>
          </div>
          <div>
            <label className="block text-[13px] uppercase tracking-wider text-ink-100">
              Payment date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </div>
          <div>
            <label className="block text-[13px] uppercase tracking-wider text-ink-100">
              Payment method
            </label>
            <div className="mt-1 space-y-2">
              <label className="flex cursor-pointer items-start gap-2 rounded-md border border-ink-500 bg-ink-700/50 px-3 py-2">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="manual"
                  checked={paymentMethod === "manual"}
                  onChange={() => setPaymentMethod("manual")}
                  className="mt-1 accent-slack-aubergine"
                />
                <span className="text-[14px] text-ink-50">
                  <strong>Manual</strong> — record only (BACS, cheque, cash already sent)
                </span>
              </label>
              <label
                className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 ${
                  stripeAvailable
                    ? "border-ink-500 bg-ink-700/50"
                    : "border-ink-700/50 bg-ink-800/50 cursor-not-allowed opacity-50"
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="stripe"
                  checked={paymentMethod === "stripe"}
                  onChange={() => setPaymentMethod("stripe")}
                  disabled={!stripeAvailable}
                  className="mt-1 accent-slack-aubergine"
                />
                <span className="text-[14px] text-ink-50">
                  <strong>Pay via Stripe (Instant)</strong>
                  {stripeAvailable ? (
                    <>
                      {" — "}
                      {amountNum > 0 ? (
                        <>
                          1.5% fee ({formatMoney(stripeFee, statement.currencySymbol)})
                          absorbed by Breeze
                        </>
                      ) : (
                        <>1.5% fee absorbed by Breeze</>
                      )}
                    </>
                  ) : (
                    <>
                      {" — "}
                      {stripeStatus.data
                        ? `tech not Stripe-ready (${stripeStatus.data.displayLabel})`
                        : "Stripe not configured for this tech"}
                    </>
                  )}
                </span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-[13px] uppercase tracking-wider text-ink-100">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </div>
        </div>
        {stripeConfirming && paymentMethod === "stripe" && (
          <div className="mt-4 rounded-md border border-stage-opportunity/40 bg-stage-opportunity/10 p-3 text-[14px] text-ink-50">
            <strong className="text-stage-opportunity">Confirm Stripe payout.</strong>{" "}
            About to send {formatMoney(amountNum, statement.currencySymbol)} to{" "}
            {statement.technicianName}'s bank via Stripe Instant Payout. Breeze pays{" "}
            {formatMoney(stripeFee, statement.currencySymbol)} in fees. This cannot be
            undone.
          </div>
        )}
        {m.isError && (
          <div className="mt-3 text-[13px] text-slack-pink">
            {(m.error as Error).message}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={() => {
              if (stripeConfirming) {
                setStripeConfirming(false);
              } else {
                onClose();
              }
            }}
            className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700"
          >
            {stripeConfirming ? "Back" : "Cancel"}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!valid || m.isPending}
            className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
          >
            {m.isPending
              ? paymentMethod === "stripe"
                ? "Sending via Stripe…"
                : "Recording…"
              : paymentMethod === "stripe"
                ? stripeConfirming
                  ? "Confirm + Send"
                  : "Pay via Stripe"
                : "Record payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SetOpeningBalanceModal({
  technicianId,
  currencySymbol,
  onClose,
}: {
  technicianId: number;
  currencySymbol: string;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [asOfDate, setAsOfDate] = useState(today);
  const [balance, setBalance] = useState("0.00");
  const [notes, setNotes] = useState("");
  const m = useMutation({
    mutationFn: () =>
      setEarningsOpeningBalance(technicianId, {
        asOfDate,
        balance: Number(balance),
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["earnings"] });
      onClose();
    },
  });

  const valid =
    Number.isFinite(Number(balance)) && /^\d{4}-\d{2}-\d{2}$/.test(asOfDate);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-4">
      <div className="w-full max-w-md rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card">
        <h2 className="text-xl font-semibold text-ink-50">Set opening balance</h2>
        <p className="mt-1 text-[15px] text-ink-50">
          One-off seed from your existing Excel sheet. Use the day before the first
          period this dashboard should compute.
        </p>
        <div className="mt-4 space-y-3 text-[15px]">
          <div>
            <label className="block text-[13px] uppercase tracking-wider text-ink-100">
              Balance ({currencySymbol})
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
            <p className="mt-1 text-[13px] text-ink-100">
              Positive = Breeze owes them. Negative = they owe Breeze.
            </p>
          </div>
          <div>
            <label className="block text-[13px] uppercase tracking-wider text-ink-100">
              As of date
            </label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </div>
          <div>
            <label className="block text-[13px] uppercase tracking-wider text-ink-100">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50"
            />
          </div>
        </div>
        {m.isError && (
          <div className="mt-3 text-[13px] text-slack-pink">
            {(m.error as Error).message}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700"
          >
            Cancel
          </button>
          <button
            onClick={() => m.mutate()}
            disabled={!valid || m.isPending}
            className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
          >
            {m.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
